import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {css} from "@emotion/react";
import {Button} from "@pg-design/button";
import {Col, Container, Row} from "@pg-design/grid";
import {
    flex,
    flexAlignCenter,
    flexDirection,
    listUnStyled,
    mb,
    mt,
    onDesktop,
    ph,
    pt,
    textAlign,
    w100
} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader";
import {Text} from "@pg-design/text";
import {compile} from "path-to-regexp";

import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {breakpoints} from "../../../common/app/styles/breakpoints";
import {LANDING_MARKET_NEWS_CATEGORY_SLUG, useLandingMarketNewsArticles} from "../hooks/useLandingMarketNewsArticles";
import {LANDING_MARKET_NEWS_COLORS, LANDING_WIDE_COL_PROPS} from "./landingMarketNews.constants";
import {LandingMarketNewsCard} from "./LandingMarketNewsCard";

const CARD_GAP_PX = 21;
const {panelLight} = LANDING_MARKET_NEWS_COLORS;

export const LandingMarketNewsArticles = () => {
    const {t, i18n} = useTranslation();
    const {recentArticles, recentLoading, recentEmpty} = useLandingMarketNewsArticles();

    const toCategoryPath = useMemo(
        () => compile(chartsRoutes[i18n.language].newshub.marketInformation.category.root),
        [i18n.language]
    );
    const categoryUrl = toCategoryPath({marketInfoCategorySlug: LANDING_MARKET_NEWS_CATEGORY_SLUG});

    if (recentEmpty) {
        return null;
    }

    return (
        <Container as="section" css={section}>
            <Row>
                <Col {...LANDING_WIDE_COL_PROPS}>
                    <Text css={title} as="h2" variant="headline_1">
                        {t("landing.market_news.title")}
                    </Text>

                    <Text css={subtitle} variant="headline_2">
                        {t("landing.market_news.subtitle")}
                    </Text>

                    {recentLoading ? (
                        <div css={loaderWrapper}>
                            <Loader />
                        </div>
                    ) : (
                        <ul css={cardsRow}>
                            {recentArticles.map((article) => (
                                <li css={cardItem} key={article.id}>
                                    <LandingMarketNewsCard article={article} language={i18n.language} />
                                </li>
                            ))}
                        </ul>
                    )}

                    <div css={ctaWrapper}>
                        <Link to={categoryUrl}>
                            <Button css={ctaButton} variant="highlight_primary">
                                {t("landing.market_news.cta")}
                            </Button>
                        </Link>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

const section = css`
    ${w100};
    background: ${panelLight};
    ${mb(4)};
    ${pt(7)};
    ${ph(1)};
`;

const title = css`
    margin: 0;
`;

const subtitle = css`
    font-weight: 400;
    ${mb(4)};

    ${onDesktop(css`
        ${mb(5)};
    `)};
`;

const cardsRow = css`
    ${listUnStyled};
    ${flex()};
    ${flexDirection("column")};
    align-items: stretch;
    gap: ${CARD_GAP_PX}px;
    margin: 0;
    ${w100};

    @media (min-width: ${breakpoints.sm}) {
        flex-direction: row;
    }
`;

const cardItem = css`
    ${w100};
    min-width: 0;

    @media (min-width: ${breakpoints.sm}) {
        flex: 1 1 calc((100% - ${2 * CARD_GAP_PX}px) / 3);
    }
`;

const loaderWrapper = css`
    ${flexAlignCenter};
    ${flexDirection("column")};
    min-height: 200px;
    ${mb(5)};
`;

const ctaWrapper = css`
    ${textAlign("center")};
    ${mt(5)};

    ${onDesktop(css`
        ${mt(6)};
    `)};
`;

const ctaButton = css`
    && {
        text-transform: uppercase;
    }
`;
